'use client'

import { DIDOMI_IDS, type DidomiIds } from '@/app/util/gtm'
import type { Didomi } from '@/util/analytics'

import type { FreestarSuppressionReason } from './types'

export type FreestarConsentResult =
    | { status: 'allowed' }
    | {
          status: 'suppressed'
          reason: Extract<FreestarSuppressionReason, 'consent_suppressed' | 'didomi_unavailable'>
      }

const freestarConsentPromises = new Map<string, Promise<FreestarConsentResult>>()

function getDidomiConsentIds(didomiIds: DidomiIds = DIDOMI_IDS) {
    return Object.values(didomiIds)
}

function hasGrantedConsent(didomi: Didomi, didomiIds?: DidomiIds) {
    const consentIds = getDidomiConsentIds(didomiIds)
    const statuses = consentIds.map((id) => didomi.getUserConsentStatusForVendor(id))
    return statuses.some((status) => status === true)
}

export function resolveFreestarConsentFromDidomi(
    didomi: Didomi | undefined,
    didomiIds: DidomiIds = DIDOMI_IDS
): Promise<FreestarConsentResult> {
    if (!didomi) {
        return Promise.resolve({ status: 'suppressed', reason: 'didomi_unavailable' })
    }

    if (didomi.isConsentRequired?.() === false) {
        return Promise.resolve({ status: 'allowed' })
    }

    if (hasGrantedConsent(didomi, didomiIds)) {
        return Promise.resolve({ status: 'allowed' })
    }

    const consentIds = getDidomiConsentIds(didomiIds)
    const observables = consentIds.map((id) => didomi.getObservableOnUserConsentStatusForVendor?.(id))
    if (observables.some((observable) => !observable)) {
        return Promise.resolve({ status: 'suppressed', reason: 'didomi_unavailable' })
    }

    return new Promise((resolve) => {
        let resolved = false
        const statuses = new Map<string, boolean>()
        observables.forEach((observable, index) => {
            observable?.subscribe((consentStatusForVendor) => {
                if (resolved || consentStatusForVendor === undefined) {
                    return
                }
                if (consentStatusForVendor) {
                    resolved = true
                    resolve({ status: 'allowed' })
                    return
                }
                statuses.set(consentIds[index], false)
                if (statuses.size === consentIds.length) {
                    resolved = true
                    resolve({ status: 'suppressed', reason: 'consent_suppressed' })
                }
            })
        })
    })
}

export function waitForFreestarConsent(didomiIds: DidomiIds = DIDOMI_IDS): Promise<FreestarConsentResult> {
    if (typeof window === 'undefined') {
        return Promise.resolve({ status: 'suppressed', reason: 'didomi_unavailable' })
    }

    const consentKey = getDidomiConsentIds(didomiIds).join(':')
    const cachedConsent = freestarConsentPromises.get(consentKey)
    if (cachedConsent) {
        return cachedConsent
    }

    const consentPromise = waitForFreestarConsentResult(didomiIds)
    freestarConsentPromises.set(consentKey, consentPromise)
    return consentPromise
}

function waitForFreestarConsentResult(didomiIds: DidomiIds = DIDOMI_IDS): Promise<FreestarConsentResult> {
    if (window.Didomi) {
        return resolveFreestarConsentFromDidomi(window.Didomi, didomiIds)
    }

    window.didomiOnReady = window.didomiOnReady || []
    return new Promise((resolve) => {
        window.didomiOnReady.push((didomi) => {
            resolve(resolveFreestarConsentFromDidomi(didomi, didomiIds))
        })
    })
}
